// Utils
import { default as classnames, default as classNames } from 'classnames'
import { useEffect } from 'react'

import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import useNavigation from '../../hooks/useNavigation'
import Icon from '../icon'

import NextIcon from './icons/next.inline.svg'
import styles from './pagination.module.scss'

export enum PAGINATION_TYPES {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export interface PaginationProps {
  totalPages: number
  page: number
  className?: string
  onChange: (page: number) => void
  hideArrows: boolean
  type?: PAGINATION_TYPES
  ariaLabelNextButton?: string
  ariaLabelPrevButton?: string
  ariaLabelPageButton?: string
  trackingMetaData?: TrackingMetaData
  productProperties?: ProductProperties
  trackReviewPagination?: boolean
}

const getPages = (currentPosition: number, totalPages: number) => {
  if (currentPosition <= 3) {
    return new Array(Math.min(totalPages, 4)).fill('').map((_, idx) => idx + 1)
  }
  if (currentPosition < totalPages) {
    return [currentPosition - 2, currentPosition - 1, currentPosition, currentPosition + 1]
  }
  return [currentPosition - 3, currentPosition - 2, currentPosition - 1, currentPosition]
}

const trackReviewPageNavigatedEvent = (
  productProperties: ProductProperties | undefined,
  page_number: number,
  navigation_type: 'page_number' | 'arrow_button',
) => {
  trackEvent(TELEMETRY_EVENT_NAMES.REVIEW_PAGE_NAVIGATED, {
    page_number,
    navigation_type,
    productProperties,
  })
}

export const Pagination = (props: PaginationProps) => {
  const {
    onChange,
    ariaLabelNextButton = 'Next',
    ariaLabelPrevButton = 'Previous',
    ariaLabelPageButton = 'Page',
    className = '',
    page = 1,
    type = PAGINATION_TYPES.PRIMARY,
    hideArrows = false,
    trackReviewPagination = false,
  } = props

  const wrapClasses = classnames(
    styles.paginationWrap,
    'd-flex justify-content--center',
    styles[`pagination__${type}`],
    className,
  )
  const prevBtnClass = classnames(
    styles.button,
    styles.icon,
    styles.icon_color__black,
    'button button--text margin-right--2',
  )
  const nextBtnClass = classnames(
    styles.button,
    styles.icon,
    styles.icon_color__black,
    'button button--text margin-left--2',
  )
  const { next, currentPosition, previous, goTo } = useNavigation({
    initialPosition: page,
    max: props.totalPages,
  })

  useEffect(() => {
    onChange(currentPosition)
  }, [currentPosition, onChange])

  useEffect(() => {
    goTo(page)
  }, [page])

  return (
    <div className={wrapClasses}>
      {!hideArrows && (
        <button
          className={prevBtnClass}
          onClick={() => {
            if (trackReviewPagination) {
              trackReviewPageNavigatedEvent(props.productProperties, currentPosition - 1, 'arrow_button')
            }
            previous()
          }}
          disabled={currentPosition === 1}
          aria-label={ariaLabelPrevButton}
        >
          <Icon iconSvg={NextIcon} className={classNames(styles.prevIcon, styles.arrowIcon)} />
        </button>
      )}
      {getPages(currentPosition, props.totalPages).map(pg => (
        <button
          className={`button button--text ${styles.paginationItemSpacing} ${pg === currentPosition ? `${styles.activePageBtn}` : ''}`}
          key={pg}
          onClick={() => {
            if (trackReviewPagination) {
              trackReviewPageNavigatedEvent(props.productProperties, pg, 'page_number')
            }
            trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
              trackGA: true,
              trackInternal: true,
              // @i18n-ignore
              category: 'navigation',
              // @i18n-ignore
              action: 'click__pagination__page',
              value: pg,
              context: props.trackingMetaData?.context,
            })
            goTo(pg)
          }}
        >
          <span className="sr-only">{ariaLabelPageButton}</span>
          {pg}
        </button>
      ))}
      {!hideArrows && (
        <button
          className={nextBtnClass}
          onClick={() => {
            if (trackReviewPagination) {
              trackReviewPageNavigatedEvent(props.productProperties, currentPosition + 1, 'arrow_button')
            }
            next()
            trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
              // @i18n-ignore
              action: 'click_next_reviews_page',
              // @i18n-ignore
              label: 'reviews_pagination',
              context: {
                productProperties: props.productProperties,
              },
              trackInternal: true,
            })
          }}
          disabled={currentPosition === props.totalPages}
          aria-label={ariaLabelNextButton}
        >
          <Icon iconSvg={NextIcon} className={classNames(styles.arrowIcon)} />
        </button>
      )}
    </div>
  )
}

export default Pagination
